import type React from "react";
import { type ReactElement, cloneElement, useMemo } from "react";

import { ChevronDownIcon } from "lucide-react";

import type { UserMe } from "simplydo/interfaces/types/userTypes";
import { Button } from "simplydo/react/ui/button";
import {
  Dropdown,
  DropdownDivider,
  DropdownItem,
  DropdownItemLink,
  DropdownMenu,
} from "simplydo/react/ui/dropdown";
import { Icon, IconData } from "simplydo/react/ui/icon";
import { Popup } from "simplydo/react/ui/popup";

import util from "utils/utils";

type RawAction<ItemType> = {
  checkPermission?: string;
  checkPermissionScope?: string;
  checkEnabledFeatures?: Parameters<typeof util.organisationFeaturesEnabled>[1];
  superadminsBypassChecks?: boolean;
  isVisible?: boolean;
  checkSuperAdminPermission?: string;
  appear?: "single" | "multi" | "both";
} & (
  | {
      type?: "Item";
      name: string;
      icon?: IconData;
      onClick?: (items: ItemType[]) => void;
      tooltip?: React.ReactNode;
      link?: (items: ItemType[]) => string;
      disabled?: boolean;
      actions?: (
        | RawAction<ItemType>
        | ((items: ItemType[]) => RawAction<ItemType>)
      )[];
    }
  | { type: "Divider" }
);

export type Action<ItemType> =
  | RawAction<ItemType>
  | ((items: ItemType[]) => RawAction<ItemType>);

export type ActionDropdownProps<ItemType> = {
  actions?:
    | Action<ItemType>[]
    | ((currentItems: ItemType[]) => Action<ItemType>[]);
  items: ItemType[];
  type: "single" | "multi";
  user: UserMe | null;
  setDropdownOpen?: (open: boolean) => void;
  trigger?: ReactElement<{
    onClick?: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  }>;
  subMenu?: { name: string; icon?: IconData };
};

const ActionDropdown = <ItemType extends Record<string, unknown>>(
  props: ActionDropdownProps<ItemType>,
) => {
  const { user: rawUser, actions: propActions, setDropdownOpen } = props;
  const user = rawUser ?? undefined;

  const actions = useMemo(() => {
    if (typeof propActions === "function") {
      return propActions(props.items);
    }
    return propActions;
  }, [props.items, propActions]);

  const multiProps =
    !props.subMenu && props.type === "multi" && !props.trigger
      ? {
          trigger: (
            <Button size="small" style={{ whiteSpace: "nowrap" }}>
              <Icon icon={ChevronDownIcon} />
              {props.items.length}
            </Button>
          ),
          style: {
            flexWrap: "nowrap",
            display: "flex",
            justifyContent: "flex-end",
            padding: 0,
          } satisfies React.CSSProperties,
        }
      : {};

  if (props.items.length === 0) {
    return null;
  }

  if (!actions?.length) {
    return <></>;
  }

  const dropdownActions = actions ? (
    actions
      .map((rawAction) => {
        if (typeof rawAction === "function") {
          return rawAction(props.items);
        }
        return rawAction;
      })
      .filter((action) => {
        if (
          action.appear &&
          action.appear !== props.type &&
          action.appear !== "both"
        ) {
          return false;
        }
        if (
          action.superadminsBypassChecks &&
          util.hasPermission(user, "super.viewDashboard")
        ) {
          return true;
        }
        if (
          action.checkSuperAdminPermission &&
          util.hasPermission(user, action.checkSuperAdminPermission)
        ) {
          return true;
        }
        if (action.checkPermission) {
          if (!user) {
            return false;
          }
          if (
            !util.hasPermission(
              user,
              action.checkPermission,
              action.checkPermissionScope ??
                user.ownerOrganisation?._id ??
                user.organisation,
            )
          ) {
            return false;
          }
        }
        if (action.checkEnabledFeatures) {
          if (!user) {
            return false;
          }
          if (
            !util.organisationFeaturesEnabled(user, action.checkEnabledFeatures)
          ) {
            return false;
          }
        }
        return true;
      })
      .map((action, index) => {
        if (action.isVisible === false) {
          return null;
        }
        if (action.type === "Divider") {
          return <DropdownDivider key={`divider-${index}`} />;
        }
        const HoverWrapper: React.FC<{ children?: ReactElement }> = ({
          children,
        }) => (
          <Popup
            trigger={children}
            position="top center"
            disabled={!action.tooltip}
          >
            {<div>{action.tooltip}</div>}
          </Popup>
        );

        if (action.actions) {
          return (
            <HoverWrapper key={action.name}>
              <ActionDropdown
                key={action.name}
                subMenu={{ name: action.name, icon: action.icon }}
                {...props}
                actions={action.actions}
              />
            </HoverWrapper>
          );
        }
        return (
          <HoverWrapper key={action.name}>
            {action.link ? (
              <DropdownItemLink
                key={action.name}
                disabled={action.disabled}
                to={action.link(props.items)}
                onClick={(e) => {
                  if (action.onClick) {
                    e.preventDefault();
                    action.onClick(props.items);
                    setDropdownOpen?.(false);
                  }
                }}
              >
                {action.icon ? <Icon icon={action.icon} size="small" /> : null}
                {action.name}
              </DropdownItemLink>
            ) : (
              <DropdownItem
                key={action.name}
                disabled={action.disabled}
                onClick={() => {
                  if (action.onClick) {
                    action.onClick(props.items);
                    setDropdownOpen?.(false);
                  }
                }}
              >
                {action.icon ? <Icon icon={action.icon} size="small" /> : null}
                {action.name}
              </DropdownItem>
            )}
          </HoverWrapper>
        );
      })
  ) : (
    <DropdownItem>No actions configured</DropdownItem>
  );

  if (props.subMenu) {
    const subMenu = props.subMenu;
    return (
      <Dropdown
        trigger={
          <DropdownItem>
            {subMenu.icon ? <Icon icon={subMenu.icon} size="small" /> : null}
            {subMenu.name}
          </DropdownItem>
        }
        direction="left"
        disabled={props.items?.length === 0}
        {...multiProps}
      >
        {props.items?.length === 0 ? null : (
          <DropdownMenu>{dropdownActions}</DropdownMenu>
        )}
      </Dropdown>
    );
  }

  return (
    <Dropdown
      direction="left"
      trigger={
        props.trigger ? (
          cloneElement(props.trigger, {
            onClick: (e) => {
              e.preventDefault();
              e.stopPropagation();
              props.trigger?.props?.onClick?.(e);
            },
          })
        ) : (
          <Button
            size="small"
            style={{ margin: 0 }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <Icon icon={ChevronDownIcon} />
          </Button>
        )
      }
      disabled={props.items?.length === 0}
      {...multiProps}
    >
      {props.items?.length === 0 ? null : (
        <DropdownMenu>{dropdownActions}</DropdownMenu>
      )}
    </Dropdown>
  );
};

export default ActionDropdown;
